import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * useIsAdmin — checks if the current authenticated user has the 'admin' role.
 * Roles live in public.user_roles and are checked via the has_role() security
 * definer function (avoids RLS recursion). The RLS policy "Users can view own
 * roles" lets us query directly without elevated privileges.
 */
export const useIsAdmin = () => {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["is-admin", user?.id],
    enabled: !!user,
    // Roles change extremely rarely — cache aggressively to avoid hitting
    // user_roles on every navigation into /admin or AppHeader re-render.
    staleTime: 30 * 60 * 1000, // 30 min
    gcTime: 60 * 60 * 1000, // 1h
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id)
        .eq("role", "admin")
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
  });

  return { isAdmin: !!data, loading: isLoading };
};
